import { Box, Stack } from '@mui/material';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { RachelUIMessage } from '../../../../rachel.types';
import RachelMarkdown from '../../../RachelMarkdown/RachelMarkdown';
import RachelVizPart from '../../../RachelMessagePart/components/RachelVizPart/RachelVizPart';
import RachelReasoningTrace from '../../../RachelReasoningTrace/RachelReasoningTrace';
import { pickLiveLabel } from '../../../RachelReasoningTrace/rachelReasoningTrace.utils';
import RachelThinkingIndicator from '../../../RachelThinkingIndicator/RachelThinkingIndicator';
import {
  RACHEL_ANSWER_ACTIONS_SPACING_TOP,
  useRachelMessageActionsStyles,
} from '../../rachelMessageItem.styles';
import RachelCopyMessageButton from '../RachelCopyMessageButton/RachelCopyMessageButton';
import RachelAskUserSummary from './components/RachelAskUserSummary';
import RachelExecuteCodeFiles from './components/RachelExecuteCodeFiles/RachelExecuteCodeFiles';
import RachelInterruptedNotice from './components/RachelInterruptedNotice/RachelInterruptedNotice';
import RachelMessageFeedback from './components/RachelMessageFeedback/RachelMessageFeedback';
import { useRachelAssistantMessage } from './useRachelAssistantMessage';

interface RachelAssistantMessageProps {
  message: RachelUIMessage;
  isLive: boolean;
  isLastMessage: boolean;
}

const RachelAssistantMessage = ({
  message,
  isLive,
  isLastMessage,
}: RachelAssistantMessageProps) => {
  const { translate } = useTranslation();
  const {
    steps,
    vizGroups,
    outputFiles,
    askUserAnswers,
    docLinkBlock,
    answerText,
    hasContent,
    isInterrupted,
  } = useRachelAssistantMessage({ message, isLive });
  const { classes } = useRachelMessageActionsStyles({
    shouldRevealOnHover: !isLastMessage,
    spacingTop: RACHEL_ANSWER_ACTIONS_SPACING_TOP,
    hasActions: !isLive && !!answerText,
  });

  if (!hasContent) {
    if (isLive) {
      return <RachelThinkingIndicator label={translate(pickLiveLabel(message.id))} />;
    }
    return isInterrupted ? <RachelInterruptedNotice /> : null;
  }

  return (
    <Stack className={classes.root}>
      <Stack sx={{ gap: 1.5, minWidth: 0 }}>
        <RachelReasoningTrace steps={steps} isLive={isLive} />
        <RachelAskUserSummary entries={askUserAnswers} />
        {!!answerText && <RachelMarkdown>{answerText}</RachelMarkdown>}
        {vizGroups.map(({ part, blocks }) => (
          <RachelVizPart key={part.toolCallId} blocks={blocks} />
        ))}
        {outputFiles.length > 0 && <RachelExecuteCodeFiles files={outputFiles} />}
        {!!docLinkBlock && <RachelVizPart blocks={[docLinkBlock]} />}
        {!isLive && isInterrupted && <RachelInterruptedNotice />}
      </Stack>
      {!isLive && !!answerText && (
        <Box className={classes.actions}>
          <RachelCopyMessageButton text={answerText} />
          <RachelMessageFeedback messageId={message.id} />
        </Box>
      )}
    </Stack>
  );
};

export default RachelAssistantMessage;
